import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, TrendingUp, TrendingDown, AlertTriangle, Package, Clock, DollarSign, BarChart3 } from 'lucide-react'
import { PageHeader } from '@/components/layout/page-header'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { useControllingDashboard } from '@/hooks/useGaeb'

const fmtCurrency = (v: number | null | undefined) => v == null || isNaN(v) ? '-' : new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(v)
const fmtPercent = (v: number | null | undefined) => v == null || isNaN(v) ? '-' : `${Math.round(v)}%`

function ProgressBar({ value, max, color = 'bg-primary-600', warningAt = 75, dangerAt = 90 }: { value: number; max: number; color?: string; warningAt?: number; dangerAt?: number }) {
  const pct = max > 0 ? (value / max) * 100 : 0
  const bar = pct > dangerAt ? 'bg-red-500' : pct > warningAt ? 'bg-yellow-500' : color
  return <div className="h-3 w-full rounded-full bg-muted"><div className={`${bar} h-3 rounded-full transition-all duration-500`} style={{ width: `${Math.min(pct, 100)}%` }} /></div>
}

interface Dashboard {
  budget: { gaeb_project_name?: string; total_offer_price?: number }
  material: { budget: number; spent: number; remaining: number; percentUsed: number }
  labor: { budgetHours: number; trackedHours: number; remaining: number; percentUsed: number }
  margin: { planned: number }
  warnings?: { severity: string; message: string }[]
  topDeviations?: { id: number; oz: string; description: string; material_gp: number; material_spent: number; deviation_percent: number }[]
}

export default function ProjektControlling() {
  const { budgetId } = useParams<{ budgetId: string }>()
  const { data: dashboard, isLoading } = useControllingDashboard(budgetId)

  if (isLoading) return <div className="space-y-4"><Skeleton className="h-8 w-48" /><div className="grid grid-cols-4 gap-4">{Array.from({length:4}).map((_,i) => <Skeleton key={i} className="h-32" />)}</div></div>
  if (!dashboard) return null

  const { budget, material, labor, margin, warnings, topDeviations } = dashboard

  return (
    <div>
      <PageHeader title="Projekt-Controlling" description={budget?.gaeb_project_name || `Budget #${budgetId}`} actions={<Link to="/admin/kalkulation"><Button variant="outline"><ArrowLeft className="h-4 w-4" /> Zurück</Button></Link>} />

      {warnings && warnings.length > 0 && <div className="mb-6 space-y-2">{warnings.map((w, i) => (
        <div key={i} className={cn('flex items-center gap-3 rounded-lg border p-3', w.severity === 'high' ? 'border-red-200 bg-red-50 dark:bg-red-950' : 'border-yellow-200 bg-yellow-50 dark:bg-yellow-950')}>
          <AlertTriangle className={cn('h-5 w-5', w.severity === 'high' ? 'text-red-500' : 'text-yellow-500')} />
          <span className="text-sm font-medium">{w.message}</span>
        </div>
      ))}</div>}

      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-4">
        {[
          { icon: Package, color: 'bg-blue-100 text-blue-600', title: 'Material', subtitle: `Budget: ${fmtCurrency(material.budget)}`, main: fmtCurrency(material.spent), label: 'ausgegeben', bar: <ProgressBar value={material.spent} max={material.budget} />, pct: fmtPercent(material.percentUsed) },
          { icon: Clock, color: 'bg-green-100 text-green-600', title: 'Arbeit', subtitle: `Budget: ${labor.budgetHours}h`, main: `${Math.round(labor.trackedHours)}h`, label: 'geleistet', bar: <ProgressBar value={labor.trackedHours} max={labor.budgetHours} color="bg-green-600" />, pct: fmtPercent(labor.percentUsed) },
          { icon: DollarSign, color: 'bg-purple-100 text-purple-600', title: 'Marge', subtitle: `Soll: ${fmtPercent(margin.planned)}`, main: fmtPercent(margin.planned), label: '', bar: null, pct: `Angebot: ${fmtCurrency(budget.total_offer_price)}` },
          { icon: BarChart3, color: 'bg-orange-100 text-orange-600', title: 'Verbleibend', subtitle: '', main: fmtCurrency(material.remaining), label: 'Material', bar: null, pct: `${Math.round(labor.remaining)}h Arbeit` },
        ].map((c, i) => (
          <Card key={i}><CardContent className="p-6">
            <div className="mb-3 flex items-center gap-3"><div className={cn('rounded-lg p-2', c.color)}><c.icon className="h-5 w-5" /></div><div><p className="text-xs uppercase text-muted-foreground">{c.title}</p>{c.subtitle && <p className="text-sm font-medium">{c.subtitle}</p>}</div></div>
            <p className="mb-1 text-lg font-bold">{c.main}{c.label && <span className="ml-1 text-sm font-normal text-muted-foreground">{c.label}</span>}</p>
            {c.bar}{c.pct && <p className="mt-1 text-xs text-muted-foreground">{c.pct}</p>}
          </CardContent></Card>
        ))}
      </div>

      {topDeviations && topDeviations.length > 0 && (
        <Card><CardContent className="p-6">
          <h3 className="mb-4 text-lg font-medium">Groesste Abweichungen</h3>
          <div className="overflow-x-auto"><table className="w-full text-sm">
            <thead><tr className="border-b">
              {['Position','Budget','Ausgegeben','Abweichung',''].map(h => <th key={h} className={cn('px-3 py-2 font-medium text-muted-foreground', h === 'Abweichung' || h === '' ? 'text-right' : 'text-left')}>{h}</th>)}
            </tr></thead>
            <tbody>{topDeviations.map(it => { const over = (it.deviation_percent || 0) > 0; return (
              <tr key={it.id} className="border-b border-border/50">
                <td className="px-3 py-2"><div className="font-mono text-xs text-muted-foreground">{it.oz}</div><div className="max-w-xs truncate">{it.description}</div></td>
                <td className="px-3 py-2 text-right">{fmtCurrency(it.material_gp)}</td>
                <td className="px-3 py-2 text-right">{fmtCurrency(it.material_spent)}</td>
                <td className="px-3 py-2 text-right"><span className={cn('flex items-center justify-end gap-1', over ? 'text-red-600' : 'text-green-600')}>{over ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}{Math.abs(Math.round(it.deviation_percent))}%</span></td>
                <td className="px-3 py-2 text-center"><span className={cn('inline-flex h-3 w-3 rounded-full', Math.abs(it.deviation_percent) > 20 ? 'bg-red-500' : Math.abs(it.deviation_percent) > 10 ? 'bg-yellow-500' : 'bg-green-500')} /></td>
              </tr>
            )})}</tbody>
          </table></div>
        </CardContent></Card>
      )}
    </div>
  )
}
